import { MarketStatus, type Market } from '@/libs/sdk/api';

import { getBinaryMarketOptions, getBinaryProbabilityPercentage } from '../market-card/binary-options';

export type EventCardRow = {
  market: Market;
  probability: number;
  yes: Market['options'][number];
  no: Market['options'][number];
};

export type RowSettlement = {
  option: Market['options'][number];
  isYes: boolean;
};

// A settled multi-market row shows its winning option (the one whose address
// matches market.result) instead of live trade buttons. Returns null while the
// market is still trading, or when the winner can't be matched to an option.
export const getRowSettlementResult = (row: EventCardRow): RowSettlement | null => {
  const { market, yes } = row;
  if (market.marketStatus < MarketStatus.settlement) return null;
  if (!market.result) return null;
  const option = market.options.find(o => o.tokenAddress === market.result);
  if (!option) return null;
  return { option, isYes: option.tokenAddress === yes.tokenAddress };
};

export type EventCardModel = {
  title: string;
  leadMarket: Market;
  totalMarkets: number;
  totalParticipants: number;
  totalVolume: number;
  nearestDeadline?: number;
  displayRows: EventCardRow[];
  remainingCount: number;
};

export const resolveEventCardHref = (_markets: Market[], eventId: string) => {
  return `/event/${eventId}`;
};

export const buildEventCardModel = (markets: Market[], title: string): EventCardModel | null => {
  const leadMarket = markets[0];
  const rows = markets
    .map(market => {
      const { yes, no } = getBinaryMarketOptions(market.options);
      if (!yes || !no) return null;

      return {
        market,
        yes,
        no,
        probability: getBinaryProbabilityPercentage(market.options) ?? 50,
      };
    })
    .filter((row): row is EventCardRow => row !== null)
    .sort((a, b) => b.probability - a.probability);

  // When the event is settled, surface the winning row first so it never gets
  // hidden below the "+N more" fold (default sort is by live probability, which
  // freezes at settlement and may not put the winner in the top rows).
  // Settlement is resolved once per row (it scans market.options) instead of
  // inside the comparator; sorting decorated pairs with the same stable sort
  // keeps ordering identical for equal keys.
  const settlements = rows.map(row => getRowSettlementResult(row));
  if (settlements.some(settlement => settlement !== null)) {
    const decorated = rows
      .map((row, index) => ({ row, won: Number(settlements[index]?.isYes ?? false) }))
      .sort((a, b) => b.won - a.won);
    decorated.forEach((entry, index) => {
      rows[index] = entry.row;
    });
  }

  if (!leadMarket) return null;

  const totalParticipants = markets.reduce((sum, market) => sum + (market.participantsCount || 0), 0);
  const totalVolume = markets.reduce((sum, market) => sum + Number(market.marketVolume || 0), 0);
  const nearestDeadline = markets.reduce<number | undefined>((min, market) => {
    const deadline = Number(market.deadline);
    if (!deadline) return min;
    return min === undefined ? deadline : Math.min(min, deadline);
  }, undefined);

  return {
    title,
    leadMarket,
    totalMarkets: markets.length,
    totalParticipants,
    totalVolume,
    nearestDeadline,
    displayRows: rows.slice(0, 3),
    remainingCount: Math.max(0, rows.length - 3),
  };
};
